import { useCallback, useEffect, useRef, useState } from "react";
import {
  ApiError,
  api,
  InventoryRow,
  IMaterialLotExtended,
  VirtualRevenueAllocation,
  VirtualRevenueCapacity,
  VirtualRevenuePreview,
} from "../api";
import { formatVND, formatNumber, formatDate } from "../utils";
import { searchItems } from "../search";
import { GlassPanel, KpiCard, Badge, SkeletonTable, ErrorState, Modal, confirm, toast } from "../components";

type FilterMode = "all" | "vat" | "bangke";

interface VirtualItem extends InventoryRow {
  is_vat: number;
  is_bangke: number;
}

const toLocalDate = (date: Date) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
};

const today = toLocalDate(new Date());

const makeIdempotencyKey = (prefix: string) =>
  `${prefix}-${Date.now()}-${crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)}`;
const errorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;
const parseMoney = (value: string) => Number(value.replace(/[^0-9]/g, "")) || 0;

/** Bounds of a calendar quarter, `to` clamped to today (no future). */
const quarterBounds = (q: 1 | 2 | 3 | 4, year: number) => {
  const starts: Record<number, string> = { 1: "01-01", 2: "04-01", 3: "07-01", 4: "10-01" };
  const ends: Record<number, string> = { 1: "03-31", 2: "06-30", 3: "09-30", 4: "12-31" };
  const from = `${year}-${starts[q]}`;
  let to = `${year}-${ends[q]}`;
  if (to > today) to = today;
  return { from, to };
};
/** today → current calendar quarter bounds (matches TaxReportPage default). */
const currentQuarterBounds = () => {
  const now = new Date(today + "T00:00:00");
  return quarterBounds((Math.floor(now.getMonth() / 3) + 1) as 1 | 2 | 3 | 4, now.getFullYear());
};
const labelForWarningCode = (code: string) => {
  const map: Record<string, string> = {
    AMBIGUOUS_IMPORT_ITEM: "Không gắn rõ 1 dòng đơn nhập",
    AMBIGUOUS_STOCK_LOT: "Nhiều lô cùng nguồn (đã nới; hiếm)",
    AMBIGUOUS_SOURCE_TYPE: "Lô vừa VAT vừa bảng kê",
    PHYSICAL_LOT_QTY_DIFFERS: "Tồn lô vật lý khác SL nhập (vẫn dùng SL nhập)",
    MISSING_INTEGER_SALE_PRICE: "Thiếu giá bán nguyên VND",
    NON_INTEGER_SOURCE_QTY: "SL nhập không nguyên dương",
    MISSING_ITEM: "SP đã xóa / không còn",
    NO_INVOICE_CLASSIFICATION_UNAVAILABLE: "Không tách DT không HĐ (dùng DT canonical)",
    CANONICAL_REVENUE_ROUNDED_TO_VND: "DT làm tròn 1đ",
    FRACTIONAL_CONSUMPTION_ROUNDED_UP: "Tiêu hao lẻ làm tròn lên",
    VALUE_OVERFLOW: "Giá trị lô tràn số",
    ORPHAN_LOT_USED_PHYSICAL_QTY: "Lô không map dòng nhập (dùng SL lô, hạn mức 0)",
  };
  return map[code] || code;
};
/** Even split of `total` integer VND across `n` buckets; remainder on the last. */
const evenSplit = (total: number, n: number): number[] => {
  if (n <= 0) return [];
  if (total <= 0) return Array(n).fill(0);
  const base = Math.floor(total / n);
  const shares = Array(n).fill(base);
  shares[n - 1] = total - base * (n - 1);
  return shares;
};

/** Trang Kho ảo VAT/Bảng kê và ledger bù doanh thu. */
export default function VirtualInventoryPage() {
  const [allItems, setAllItems] = useState<VirtualItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterMode>("all");
  const [search, setSearch] = useState("");
  const [lotItem, setLotItem] = useState<VirtualItem | null>(null);
  const [lots, setLots] = useState<IMaterialLotExtended[]>([]);
  const [lotLoading, setLotLoading] = useState(false);

  const initialBounds = currentQuarterBounds();
  const [from, setFrom] = useState(initialBounds.from);
  const [to, setTo] = useState(initialBounds.to);
  const [capacity, setCapacity] = useState<VirtualRevenueCapacity | null>(null);
  const [capacityLoading, setCapacityLoading] = useState(true);
  const [capacityError, setCapacityError] = useState<string | null>(null);
  const [history, setHistory] = useState<VirtualRevenueAllocation[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const [allocationOpen, setAllocationOpen] = useState(false);
  const [targetRevenue, setTargetRevenue] = useState(0);
  const [allocationReason, setAllocationReason] = useState("");
  const [preview, setPreview] = useState<VirtualRevenuePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewStale, setPreviewStale] = useState(false);
  const [applyKey, setApplyKey] = useState("");
  const [applying, setApplying] = useState(false);
  const previewRequestRef = useRef(0);

  const [allocMode, setAllocMode] = useState<"single" | "multi">("single");
  const [multiYear, setMultiYear] = useState<number>(Number(from.slice(0, 4)) || new Date().getFullYear());
  const [multiQuarters, setMultiQuarters] = useState<Set<1 | 2 | 3 | 4>>(new Set());
  const [multiCaps, setMultiCaps] = useState<Record<number, VirtualRevenueCapacity | null>>({});
  const [multiCapsLoading, setMultiCapsLoading] = useState(false);
  const [multiTotalTarget, setMultiTotalTarget] = useState(0);
  const [multiPlan, setMultiPlan] = useState<Array<{ q: 1 | 2 | 3 | 4; from: string; to: string; planned: number; max: number }> | null>(null);
  const [multiApplying, setMultiApplying] = useState(false);
  const [multiApplyResult, setMultiApplyResult] = useState<string | null>(null);

  const [detail, setDetail] = useState<VirtualRevenueAllocation | null>(null);
  const [reverseTarget, setReverseTarget] = useState<VirtualRevenueAllocation | null>(null);
  const [reverseReason, setReverseReason] = useState("");
  const [reverseKey, setReverseKey] = useState("");
  const [reversing, setReversing] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.inventory({ virtual_only: 1, limit: 1000 });
      const inventory = (result.inventory || []) as VirtualItem[];
      setAllItems(inventory.filter((item) => item.is_vat || item.is_bangke));
    } catch (loadError: unknown) {
      setError(errorMessage(loadError, "Không tải được kho ảo"));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCapacity = useCallback(async () => {
    if (!from || !to || from > to) {
      setCapacity(null);
      setCapacityError("Khoảng ngày không hợp lệ");
      setCapacityLoading(false);
      return;
    }
    setCapacityLoading(true);
    setCapacityError(null);
    try {
      const result = await api.virtualRevenueAllocation.capacity(from, to);
      setCapacity(result.capacity);
    } catch (loadError: unknown) {
      setCapacity(null);
      setCapacityError(errorMessage(loadError, "Không tải được hạn mức phân bổ"));
    } finally {
      setCapacityLoading(false);
    }
  }, [from, to]);

  const loadHistory = useCallback(async () => {
    if (!from || !to || from > to) {
      setHistory([]);
      setHistoryLoading(false);
      return;
    }
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const result = await api.virtualRevenueAllocation.list({ from, to, limit: 50 });
      setHistory(result.allocations || []);
    } catch (loadError: unknown) {
      setHistory([]);
      setHistoryError(errorMessage(loadError, "Không tải được lịch sử phân bổ"));
    } finally {
      setHistoryLoading(false);
    }
  }, [from, to]);

  const refreshRevenueData = useCallback(async () => {
    await Promise.all([loadCapacity(), loadHistory()]);
  }, [loadCapacity, loadHistory]);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      void refreshRevenueData();
    }, 250);
    return () => window.clearTimeout(timer);
  }, [refreshRevenueData]);

  useEffect(() => {
    if (!allocationOpen || !capacity || targetRevenue <= 0) {
      setPreview(null);
      setPreviewLoading(false);
      setPreviewError(null);
      setPreviewStale(false);
      return;
    }

    const requestId = ++previewRequestRef.current;
    setPreview(null);
    setPreviewLoading(true);
    setPreviewError(null);
    setPreviewStale(false);
    const timer = window.setTimeout(async () => {
      try {
        const result = await api.virtualRevenueAllocation.preview({
          from,
          to,
          target_revenue: targetRevenue,
        });
        if (requestId !== previewRequestRef.current) return;
        setPreview(result.preview);
        setApplyKey(makeIdempotencyKey("vra-apply"));
      } catch (previewRequestError: unknown) {
        if (requestId !== previewRequestRef.current) return;
        const isStale = previewRequestError instanceof ApiError && previewRequestError.status === 409;
        setPreviewStale(isStale);
        setPreviewError(errorMessage(previewRequestError, "Không tạo được bản xem trước"));
      } finally {
        if (requestId === previewRequestRef.current) setPreviewLoading(false);
      }
    }, 450);

    return () => {
      window.clearTimeout(timer);
      previewRequestRef.current += 1;
    };
  }, [allocationOpen, capacity, from, targetRevenue, to]);

  const openLots = async (item: VirtualItem) => {
    setLotItem(item);
    setLots([]);
    setLotLoading(true);
    try {
      const result = await api.itemLots(item.item_id);
      setLots(result.lots || []);
    } catch {
      setLots([]);
    } finally {
      setLotLoading(false);
    }
  };

  const openAllocation = () => {
    if (!capacity || capacity.max_allocatable_revenue <= 0) return;
    setTargetRevenue(capacity.max_allocatable_revenue);
    setAllocationReason("");
    setPreview(null);
    setPreviewError(null);
    setPreviewStale(false);
    setAllocMode("single");
    setMultiYear(Number(from.slice(0, 4)) || new Date().getFullYear());
    setMultiQuarters(new Set());
    setMultiCaps({});
    setMultiTotalTarget(0);
    setMultiPlan(null);
    setMultiApplyResult(null);
    setAllocationOpen(true);
  };

  const reloadStalePreview = async () => {
    setPreviewStale(false);
    setPreviewError(null);
    await loadCapacity();
  };

  const availableQuarters = (year: number): Array<1 | 2 | 3 | 4> => {
    const months = new Date(today + "T00:00:00");
    const curYear = months.getFullYear();
    const curQ = Math.floor(months.getMonth() / 3) + 1;
    const all: Array<1 | 2 | 3 | 4> = [1, 2, 3, 4];
    if (year < curYear) return all;
    if (year > curYear) return [];
    return all.filter((q) => q <= curQ);
  };

  const toggleMultiQuarter = (q: 1 | 2 | 3 | 4) => {
    setMultiQuarters((prev) => {
      const next = new Set(prev);
      if (next.has(q)) next.delete(q);
      else next.add(q);
      return next;
    });
  };

  const loadMultiCaps = useCallback(async () => {
    const qs = Array.from(multiQuarters).sort((a, b) => a - b);
    if (qs.length === 0) {
      setMultiCaps({});
      return;
    }
    setMultiCapsLoading(true);
    try {
      const entries = await Promise.all(
        qs.map(async (q) => {
          const { from: qf, to: qt } = quarterBounds(q, multiYear);
          try {
            const r = await api.virtualRevenueAllocation.capacity(qf, qt);
            return [q, r.capacity] as const;
          } catch {
            return [q, null] as const;
          }
        }),
      );
      const next: Record<number, VirtualRevenueCapacity | null> = {};
      for (const [q, c] of entries) next[q] = c;
      setMultiCaps(next);
    } finally {
      setMultiCapsLoading(false);
    }
  }, [multiQuarters, multiYear]);

  useEffect(() => {
    void loadMultiCaps();
  }, [loadMultiCaps]);

  // Recompute the even-split plan whenever inputs change (debounce-lite via 0ms timeout).
  useEffect(() => {
    const qs = Array.from(multiQuarters).sort((a, b) => a - b);
    if (allocMode !== "multi" || qs.length === 0) {
      setMultiPlan(null);
      return;
    }
    const timer = window.setTimeout(() => {
      const shares = evenSplit(multiTotalTarget, qs.length);
      const plan = qs.map((q, i) => {
        const cap = multiCaps[q];
        const max = cap?.max_allocatable_revenue ?? 0;
        const { from: qf, to: qt } = quarterBounds(q, multiYear);
        return { q, from: qf, to: qt, planned: Math.min(shares[i], max), max };
      });
      setMultiPlan(plan);
    }, 50);
    return () => window.clearTimeout(timer);
  }, [allocMode, multiQuarters, multiTotalTarget, multiCaps, multiYear]);

  const multiSumMax = ((): number => {
    if (!multiPlan) return 0;
    return multiPlan.reduce((s, p) => s + p.max, 0);
  })();
  const multiSumPlanned = ((): number => {
    if (!multiPlan) return 0;
    return multiPlan.reduce((s, p) => s + p.planned, 0);
  })();

  const applyMulti = async () => {
    if (!multiPlan || multiPlan.length === 0 || !allocationReason.trim() || multiApplying) return;
    if (multiPlan.every((p) => p.planned <= 0)) return;
    const accepted = await confirm({
      title: "Xác nhận chia đều theo quý",
      message: `Bù tổng ${formatVND(multiSumPlanned)} chia đều cho ${multiPlan.filter((p) => p.planned > 0).length} quý (áp dụng tuần tự, re-check hạn mức mỗi quý). Không trừ tồn POS/Loyverse. Tiếp tục?`,
      confirmLabel: "Xác nhận",
    });
    if (!accepted) return;

    setMultiApplying(true);
    setMultiApplyResult(null);
    const results: Array<{ q: number; amount: number; status: string }> = [];
    try {
      for (const p of multiPlan) {
        if (p.planned <= 0) {
          results.push({ q: p.q, amount: 0, status: "bỏ (0)" });
          continue;
        }
        let cap: VirtualRevenueCapacity | null;
        try {
          cap = (await api.virtualRevenueAllocation.capacity(p.from, p.to)).capacity;
        } catch {
          results.push({ q: p.q, amount: 0, status: "lỗi tải hạn mức" });
          break;
        }
        const clamped = Math.min(p.planned, cap.max_allocatable_revenue);
        if (clamped <= 0) {
          results.push({ q: p.q, amount: 0, status: "không đủ hạn mức" });
          continue;
        }
        try {
          let prev = await api.virtualRevenueAllocation.preview({ from: p.from, to: p.to, target_revenue: clamped });
          let applied = false;
          for (let attempt = 0; attempt < 2 && !applied; attempt++) {
            try {
              const res = await api.virtualRevenueAllocation.apply({
                from: p.from,
                to: p.to,
                target_revenue: prev.preview.target_revenue,
                plan_hash: prev.preview.plan_hash,
                idempotency_key: makeIdempotencyKey(`vra-q${p.q}-${multiYear}`),
                reason: `${allocationReason.trim()} · Q${p.q}/${multiYear}`,
              });
              results.push({ q: p.q, amount: res.allocation.allocated_revenue, status: "ok" });
              applied = true;
            } catch (applyError: unknown) {
              if (applyError instanceof ApiError && applyError.status === 409 && attempt === 0) {
                // stale: refresh preview once and retry
                prev = await api.virtualRevenueAllocation.preview({ from: p.from, to: p.to, target_revenue: clamped });
              } else {
                throw applyError;
              }
            }
          }
        } catch {
          results.push({ q: p.q, amount: 0, status: "lỗi áp dụng" });
          break;
        }
      }
      const summary = results
        .map((r) => `Q${r.q}: ${r.status === "ok" ? formatVND(r.amount) : r.status}`)
        .join("; ");
      setMultiApplyResult(summary);
      if (results.some((r) => r.status === "ok")) {
        toast.success(`Đã chia đều: ${summary}`);
      } else {
        toast.error(`Không hoàn tất: ${summary}`);
      }
      await refreshRevenueData();
    } finally {
      setMultiApplying(false);
    }
  };

  const applyAllocation = async () => {
    if (!preview || !capacity || !allocationReason.trim() || applying) return;
    const itemNames = new Set(preview.items.map((item) => item.item_id)).size;
    const accepted = await confirm({
      title: "Xác nhận bù doanh thu",
      message: `Phân bổ ${formatVND(preview.allocated_revenue)} từ ${preview.items.length} lô / ${itemNames} sản phẩm. Thao tác này chỉ trừ hạn mức kho ảo, tuyệt đối không trừ tồn POS, D1 inventory hay Loyverse. Tiếp tục?`,
      confirmLabel: "Xác nhận phân bổ",
    });
    if (!accepted) return;

    setApplying(true);
    try {
      const result = await api.virtualRevenueAllocation.apply({
        from,
        to,
        target_revenue: preview.target_revenue,
        plan_hash: preview.plan_hash,
        idempotency_key: applyKey || makeIdempotencyKey("vra-apply"),
        reason: allocationReason.trim(),
      });
      toast.success(`Đã phân bổ ${formatVND(result.allocation.allocated_revenue)}; tồn POS/Loyverse không đổi.`);
      if (result.s1a_regen.status === "error") {
        toast.warning("Đã ghi ledger nhưng tái sinh S1A đang lỗi; dữ liệu phân bổ vẫn được giữ an toàn.");
      }
      setAllocationOpen(false);
      setPreview(null);
      await refreshRevenueData();
    } catch (applyError: unknown) {
      if (applyError instanceof ApiError && applyError.status === 409) {
        setPreview(null);
        setPreviewStale(true);
        setPreviewError("Bản xem trước đã cũ vì doanh thu hoặc hạn mức vừa thay đổi. Hãy tải và xem trước lại.");
        await loadCapacity();
      } else {
        toast.error(errorMessage(applyError, "Không áp dụng được phân bổ"));
      }
    } finally {
      setApplying(false);
    }
  };

  const reverseAllocation = async () => {
    if (!reverseTarget || !reverseReason.trim() || reversing) return;
    const accepted = await confirm({
      title: "Lập bút toán đảo",
      message: `Đảo phân bổ ${formatVND(reverseTarget.allocated_revenue)}? Lịch sử gốc không bị xóa; hạn mức kho ảo được hoàn lại và tồn POS/Loyverse vẫn không đổi.`,
      danger: true,
      confirmLabel: "Lập bút toán đảo",
    });
    if (!accepted) return;

    setReversing(true);
    try {
      const result = await api.virtualRevenueAllocation.reverse(reverseTarget.id, {
        idempotency_key: reverseKey || makeIdempotencyKey(`vra-reverse-${reverseTarget.id}`),
        reason: reverseReason.trim(),
      });
      toast.success("Đã lập bút toán đảo; lịch sử gốc được giữ nguyên.");
      if (result.s1a_regen.status === "error") {
        toast.warning("Bút toán đảo đã ghi nhưng tái sinh S1A đang lỗi.");
      }
      setReverseTarget(null);
      setReverseReason("");
      setReverseKey("");
      setDetail(null);
      await refreshRevenueData();
    } catch (reverseError: unknown) {
      toast.error(errorMessage(reverseError, "Không lập được bút toán đảo"));
      if (reverseError instanceof ApiError && reverseError.status === 409) {
        await refreshRevenueData();
      }
    } finally {
      setReversing(false);
    }
  };

  const filteredByType = filter === "vat"
    ? allItems.filter((item) => item.is_vat)
    : filter === "bangke"
      ? allItems.filter((item) => item.is_bangke)
      : allItems;
  const filtered = searchItems(filteredByType, search, (item) => ({
    id: item.item_id,
    barcode: item.barcode,
    sku: item.sku,
    name: item.item_name,
  })).map(({ item }) => item);
  const totalValue = filtered.reduce((sum, item) => sum + item.in_stock * item.default_price, 0);
  const maxRevenue = capacity?.max_allocatable_revenue || 0;

  const setClampedTarget = (value: number) => {
    setTargetRevenue(Math.min(Math.max(Math.round(value), 0), maxRevenue));
  };

  const handleExportCSV = () => {
    const header = "Tên,SKU,VAT,Bảng kê,Tồn kho,Giá bán,Giá trị tồn\n";
    const rows = filtered.map((item) =>
      `"${item.item_name}","${item.sku || ""}",${item.is_vat ? "1" : "0"},${item.is_bangke ? "1" : "0"},${item.in_stock},${item.default_price},${item.in_stock * item.default_price}`
    ).join("\n");
    const blob = new Blob(["﻿" + header + rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `kho-ao-${filter}-${today}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <GlassPanel title="Bù doanh thu vào S1A" badge={`${from} → ${to}`}>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end", marginBottom: 16 }}>
          <label style={{ minWidth: 145, flex: "1 1 145px", fontSize: 12, color: "var(--text-secondary)" }}>
            Từ ngày
            <input type="date" value={from} max={to} onChange={(event) => setFrom(event.target.value)} style={{ marginTop: 5 }} />
          </label>
          <label style={{ minWidth: 145, flex: "1 1 145px", fontSize: 12, color: "var(--text-secondary)" }}>
            Đến ngày
            <input type="date" value={to} min={from} max={today} onChange={(event) => setTo(event.target.value)} style={{ marginTop: 5 }} />
          </label>
          <button className="btn btn-secondary" onClick={() => void refreshRevenueData()} disabled={capacityLoading || historyLoading}>
            <span className="material-symbols-outlined" style={{ fontSize: 17 }}>refresh</span>
            Làm mới
          </button>
          <button className="btn btn-primary" onClick={openAllocation} disabled={!capacity || maxRevenue <= 0 || capacityLoading}>
            <span className="material-symbols-outlined" style={{ fontSize: 17 }}>account_balance</span>
            Bù doanh thu
          </button>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flex: "1 1 100%" }}>
            <span style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Quý:</span>
            {([1, 2, 3, 4] as const).map((q) => {
              const { from: qf, to: qt } = quarterBounds(q, Number(from.slice(0, 4)) || new Date().getFullYear());
              const active = from === qf && to === qt;
              return (
                <button
                  key={q}
                  type="button"
                  className={`tab-item${active ? " active" : ""}`}
                  style={{ padding: "4px 12px", fontSize: 12 }}
                  onClick={() => { setFrom(qf); setTo(qt); }}
                  aria-pressed={active}
                >
                  Q{q}
                </button>
              );
            })}
          </div>
        </div>

        {capacityLoading ? (
          <SkeletonTable rows={1} cols={4} />
        ) : capacityError ? (
          <ErrorState error={capacityError} onRetry={loadCapacity} />
        ) : (
          <div className="kpi-grid" style={{ marginBottom: 0 }}>
            <KpiCard label="Doanh thu đã bán (canonical)" value={formatVND(capacity?.canonical_sales_revenue || 0)} note="Theo khoảng ngày" dotColor="blue" />
            <KpiCard label="Đã phân bổ vào S1A" value={formatVND(capacity?.active_allocated_revenue || 0)} note="Allocation đang hiệu lực" dotColor="ok" />
            <KpiCard label="Còn chưa phân bổ" value={formatVND(capacity?.canonical_unallocated_revenue || 0)} note="Doanh thu canonical còn lại" dotColor="warn" />
            <KpiCard label="Kho VAT/BK còn dùng được" value={formatVND(capacity?.available_virtual_value || 0)} note={`Tối đa lần này ${formatVND(maxRevenue)}`} dotColor="blue" />
          </div>
        )}

        {!!capacity?.warnings.length && (
          <div style={{ marginTop: 12, padding: 12, border: "1px solid var(--accent-amber)", borderRadius: 8, color: "var(--text-secondary)", fontSize: 12 }}>
            <details>
              <summary style={{ cursor: "pointer" }}>
                <b>{capacity.warnings.length} cảnh báo nguồn</b> · nhóm theo loại
              </summary>
              <ul style={{ margin: "8px 0 0 18px", padding: 0 }}>
                {Array.from(new Set(capacity.warnings.map((w) => w.code))).map((code) => {
                  const ws = capacity.warnings.filter((w) => w.code === code);
                  const samples = ws
                    .filter((w) => w.import_order_id || w.stock_lot_id || w.item_name)
                    .slice(0, 3)
                    .map((w) => {
                      const parts: string[] = [];
                      if (w.import_order_id) parts.push(`đơn #${w.import_order_id}`);
                      if (w.item_name) parts.push(w.item_name);
                      if (w.stock_lot_id) parts.push(`lô #${w.stock_lot_id}`);
                      return parts.join(" · ");
                    });
                  return (
                    <li key={code} style={{ marginBottom: 6 }}>
                      <b>{labelForWarningCode(code)}</b> ×{ws.length}
                      <span style={{ color: "var(--text-tertiary)" }}> — {code}</span>
                      {samples.length > 0 && (
                        <ul style={{ margin: "2px 0 0 18px", padding: 0, color: "var(--text-tertiary)" }}>
                          {samples.map((s, i) => (
                            <li key={i}>{s}</li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            </details>
          </div>
        )}
        <ul style={{ margin: "12px 0 0", padding: 0, listStyle: "none", color: "var(--text-tertiary)", fontSize: 12 }}>
          <li style={{ marginBottom: 2 }}>Kho thật (POS/kệ): tồn bán hàng — bù <b>không</b> trừ.</li>
          <li style={{ marginBottom: 2 }}>Kho ảo VAT/BK: hạn mức chứng từ để bốc khai S1A — bù chỉ trừ hạn mức này.</li>
          <li>Tiền S1A = doanh thu bán thật; hàng ảo bốc theo FIFO giá trị, không cần khớp SP khách mua.</li>
        </ul>
      </GlassPanel>

      <GlassPanel title="Lịch sử phân bổ" badge={`${history.length} lần`}>
        {historyLoading ? (
          <SkeletonTable rows={4} cols={6} />
        ) : historyError ? (
          <ErrorState error={historyError} onRetry={loadHistory} />
        ) : history.length === 0 ? (
          <div className="empty">Chưa có phân bổ trong khoảng ngày này.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Thời gian</th>
                  <th>Khoảng doanh thu</th>
                  <th style={{ textAlign: "right" }}>VAT</th>
                  <th style={{ textAlign: "right" }}>Bảng kê</th>
                  <th style={{ textAlign: "right" }}>Tổng phân bổ</th>
                  <th>Người thao tác / lý do</th>
                  <th>Trạng thái</th>
                </tr>
              </thead>
              <tbody>
                {history.map((allocation) => {
                  const vatValue = (allocation.items || []).filter((item) => item.sub_type === "vat").reduce((sum, item) => sum + item.allocated_value, 0);
                  const bangkeValue = (allocation.items || []).filter((item) => item.sub_type === "bangke").reduce((sum, item) => sum + item.allocated_value, 0);
                  return (
                    <tr key={allocation.id} onClick={() => setDetail(allocation)} style={{ cursor: "pointer", opacity: allocation.status === "reversed" ? 0.7 : 1 }} title="Bấm xem chi tiết">
                      <td>{formatDate(allocation.created_at)}</td>
                      <td>{allocation.date_from} → {allocation.date_to}</td>
                      <td style={{ textAlign: "right" }}>{formatVND(vatValue)}</td>
                      <td style={{ textAlign: "right" }}>{formatVND(bangkeValue)}</td>
                      <td style={{ textAlign: "right", fontWeight: 700 }}>{formatVND(allocation.allocated_revenue)}</td>
                      <td><b>{allocation.created_by}</b><br /><span style={{ color: "var(--text-tertiary)", fontSize: 12 }}>{allocation.reason}</span></td>
                      <td>
                        <Badge variant={allocation.status === "active" ? "ok" : "ghost"}>{allocation.status === "active" ? "Hiệu lực" : "Đã đảo"}</Badge>{" "}
                        {allocation.s1a_regen_status === "error" ? <Badge variant="bad">S1A lỗi</Badge> : allocation.s1a_regen_status === "pending" ? <Badge variant="warn">S1A chờ</Badge> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </GlassPanel>

      <GlassPanel title="Kho ảo (VAT / Bảng kê)" badge={`${filtered.length} SP`}>
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
          <div className="tabs">
            {(["all", "vat", "bangke"] as FilterMode[]).map((mode) => (
              <button key={mode} className={`tab-item ${filter === mode ? "active" : ""}`} onClick={() => setFilter(mode)}>
                {mode === "all" ? "Tất cả" : mode === "vat" ? "VAT" : "Bảng kê"}
              </button>
            ))}
          </div>
          <div style={{ flex: 1 }} />
          <div className="search" style={{ maxWidth: 240 }}>
            <span className="material-symbols-outlined">search</span>
            <input placeholder="Tìm SP…" value={search} onChange={(event) => setSearch(event.target.value)} />
          </div>
          <button className="btn btn-secondary" onClick={handleExportCSV}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>download</span>
            Export CSV
          </button>
        </div>

        {loading ? (
          <SkeletonTable rows={6} cols={4} />
        ) : error ? (
          <ErrorState error={error} onRetry={loadItems} />
        ) : filtered.length === 0 ? (
          <div className="empty">Không có sản phẩm kho ảo{filter !== "all" ? ` (${filter})` : ""}</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th style={{ width: 32 }}>®</th><th>Tên sản phẩm</th><th>SKU</th><th style={{ textAlign: "center" }}>Loại</th><th style={{ textAlign: "right" }}>Tồn kho</th><th style={{ textAlign: "right" }}>Giá bán</th><th style={{ textAlign: "right" }}>Giá trị</th></tr></thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={`${item.item_id}_${item.variant_id}`} onClick={() => void openLots(item)} style={{ cursor: "pointer" }} title="Bấm xem lô nhập (FIFO)">
                    <td style={{ textAlign: "center", color: "var(--accent-emerald)" }}>®</td>
                    <td style={{ fontWeight: 600 }}>{item.item_name}</td>
                    <td style={{ color: "var(--text-tertiary)", fontSize: 12 }}>{item.sku || "—"}</td>
                    <td style={{ textAlign: "center" }}>{item.is_vat ? <Badge variant="ok">VAT</Badge> : null} {item.is_bangke ? <Badge variant="warn">BK</Badge> : null}</td>
                    <td style={{ textAlign: "right" }}>{formatNumber(item.in_stock)}</td>
                    <td style={{ textAlign: "right" }}>{formatVND(item.default_price)}</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>{formatVND(item.in_stock * item.default_price)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr style={{ fontWeight: 700 }}><td colSpan={4}>Tổng cộng: {filtered.length} sản phẩm</td><td style={{ textAlign: "right" }}>{formatNumber(filtered.reduce((sum, item) => sum + item.in_stock, 0))}</td><td></td><td style={{ textAlign: "right" }}>{formatVND(totalValue)}</td></tr></tfoot>
            </table>
          </div>
        )}
      </GlassPanel>

      <Modal
        open={allocationOpen}
        onClose={() => !applying && setAllocationOpen(false)}
        title="Bù doanh thu bằng kho ảo"
        width={1000}
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setAllocationOpen(false)} disabled={applying || multiApplying}>Huỷ</button>
            {allocMode === "single" ? (
              <button className="btn btn-primary" onClick={() => void applyAllocation()} disabled={!preview || !allocationReason.trim() || applying || previewLoading || previewStale}>
                {applying ? "Đang áp dụng…" : "Xác nhận phân bổ"}
              </button>
            ) : (
              <button className="btn btn-primary" onClick={() => void applyMulti()} disabled={!multiPlan || multiApplying || !allocationReason.trim() || multiPlan.every((p) => p.planned <= 0)}>
                {multiApplying ? "Đang áp dụng…" : "Chia đều & áp dụng"}
              </button>
            )}
          </>
        }
      >
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 18 }}>
          <div><span style={{ color: "var(--text-tertiary)", fontSize: 12 }}>Khoảng ngày</span><br /><b>{from} → {to}</b></div>
          <div><span style={{ color: "var(--text-tertiary)", fontSize: 12 }}>Doanh thu canonical</span><br /><b>{formatVND(capacity?.canonical_sales_revenue || 0)}</b></div>
          <div><span style={{ color: "var(--text-tertiary)", fontSize: 12 }}>Còn chưa phân bổ</span><br /><b>{formatVND(capacity?.canonical_unallocated_revenue || 0)}</b></div>
          <div><span style={{ color: "var(--text-tertiary)", fontSize: 12 }}>Kho VAT/BK khả dụng</span><br /><b>{formatVND(capacity?.available_virtual_value || 0)}</b></div>
        </div>

        <div className="tabs" style={{ marginBottom: 14 }}>
          <button className={`tab-item${allocMode === "single" ? " active" : ""}`} onClick={() => setAllocMode("single")}>Một khoảng</button>
          <button className={`tab-item${allocMode === "multi" ? " active" : ""}`} onClick={() => setAllocMode("multi")}>Chia đều theo quý</button>
        </div>

        {allocMode === "single" && (
          <>
            <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Đưa vào S1A</label>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <input
                type="range"
                min={0}
                max={maxRevenue}
                step={1}
                value={targetRevenue}
                onChange={(event) => setClampedTarget(Number(event.target.value))}
                style={{ flex: "1 1 260px", padding: 0 }}
                aria-label="Số tiền phân bổ"
              />
              <div style={{ position: "relative", flex: "0 1 230px", minWidth: 180 }}>
                <input
                  value={formatNumber(targetRevenue)}
                  onChange={(event) => setClampedTarget(parseMoney(event.target.value))}
                  inputMode="numeric"
                  aria-label="Nhập chính xác số tiền phân bổ"
                  style={{ paddingRight: 32, textAlign: "right", fontWeight: 700 }}
                />
                <span style={{ position: "absolute", right: 12, top: 9, color: "var(--text-tertiary)" }}>đ</span>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, color: "var(--text-tertiary)", fontSize: 12, marginTop: 5 }}>
              <span>{formatVND(targetRevenue)}</span><span>Tối đa {formatVND(maxRevenue)}</span>
            </div>
          </>
        )}

        {allocMode === "multi" && (
          <>
            <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Chọn quý (năm {multiYear})</label>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
              {([1, 2, 3, 4] as const).map((q) => {
                const avail = availableQuarters(multiYear).includes(q);
                const inQ = multiQuarters.has(q);
                return (
                  <button
                    key={q}
                    type="button"
                    className={`tab-item${inQ ? " active" : ""}`}
                    style={{ padding: "4px 12px", fontSize: 12, opacity: avail ? 1 : 0.4 }}
                    disabled={!avail}
                    onClick={() => toggleMultiQuarter(q)}
                    aria-pressed={inQ}
                  >
                    Q{q}
                  </button>
                );
              })}
              <button
                type="button"
                className="btn btn-compact btn-ghost"
                style={{ fontSize: 12 }}
                onClick={() => setMultiQuarters(new Set(availableQuarters(multiYear) as Array<1 | 2 | 3 | 4>))}
                disabled={availableQuarters(multiYear).length === 0}
              >Chọn hết</button>
              <button
                type="button"
                className="btn btn-compact btn-ghost"
                style={{ fontSize: 12 }}
                onClick={() => setMultiQuarters(new Set())}
                disabled={multiQuarters.size === 0}
              >Bỏ chọn</button>
              <input
                type="number"
                value={multiYear}
                onChange={(event) => setMultiYear(Math.min(Math.max(Number(event.target.value) || new Date().getFullYear(), 2020), new Date().getFullYear()))}
                style={{ width: 80, textAlign: "right" }}
                aria-label="Năm chia đều"
                min={2020}
                max={new Date().getFullYear()}
              />
            </div>

            <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Tổng muốn bù (chia đều)</label>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <input
                type="range"
                min={0}
                max={multiSumMax}
                step={1}
                value={Math.min(multiTotalTarget, multiSumMax)}
                onChange={(event) => setMultiTotalTarget(Math.min(Number(event.target.value), multiSumMax))}
                style={{ flex: "1 1 260px", padding: 0 }}
                aria-label="Tổng tiền chia đều"
                disabled={multiSumMax <= 0 || multiCapsLoading}
              />
              <div style={{ position: "relative", flex: "0 1 230px", minWidth: 180 }}>
                <input
                  value={formatNumber(Math.min(multiTotalTarget, multiSumMax))}
                  onChange={(event) => setMultiTotalTarget(Math.min(parseMoney(event.target.value), multiSumMax))}
                  inputMode="numeric"
                  aria-label="Nhập tổng tiền chia đều"
                  style={{ paddingRight: 32, textAlign: "right", fontWeight: 700 }}
                />
                <span style={{ position: "absolute", right: 12, top: 9, color: "var(--text-tertiary)" }}>đ</span>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, color: "var(--text-tertiary)", fontSize: 12, marginTop: 5 }}>
              <span>Tổng thực tế: {formatVND(multiSumPlanned)}</span>
              <span>Tối đa {formatVND(multiSumMax)}</span>
            </div>
            {multiTotalTarget > multiSumPlanned && multiSumPlanned < multiSumMax && (
              <div style={{ marginTop: 6, fontSize: 12, color: "var(--accent-amber)" }}>
                Kho ảo/doanh thu quý không đủ; tổng thực tế = {formatVND(multiSumPlanned)}.
              </div>
            )}

            {multiCapsLoading ? (
              <div className="empty" style={{ marginTop: 18 }}>Đang tải hạn mức từng quý…</div>
            ) : multiPlan ? (
              <div style={{ marginTop: 18 }}>
                <div style={{ marginBottom: 8, color: "var(--text-secondary)", fontSize: 13 }}>
                  Kế hoạch chia đều · áp dụng tuần tự Q1→Q4 · re-check hạn mức mỗi quý
                </div>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Quý</th><th>Khoảng ngày</th><th style={{ textAlign: "right" }}>Hạn mức quý</th><th style={{ textAlign: "right" }}>Sẽ bù</th></tr></thead>
                    <tbody>
                      {multiPlan.map((p) => (
                        <tr key={p.q}>
                          <td><b>Q{p.q}</b></td>
                          <td>{p.from} → {p.to}</td>
                          <td style={{ textAlign: "right" }}>{formatVND(p.max)}</td>
                          <td style={{ textAlign: "right", fontWeight: 700, color: p.planned > 0 ? "var(--accent-emerald)" : "var(--text-tertiary)" }}>{p.planned > 0 ? formatVND(p.planned) : "—"}</td>
                        </tr>
                      ))}
                      <tr style={{ fontWeight: 700 }}>
                        <td colSpan={3}>Tổng</td>
                        <td style={{ textAlign: "right" }}>{formatVND(multiSumPlanned)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {multiApplyResult && (
                  <p style={{ marginTop: 8, fontSize: 12, color: "var(--text-secondary)" }}>Kết quả: {multiApplyResult}</p>
                )}
              </div>
            ) : (
              <div className="empty" style={{ marginTop: 18 }}>Chọn ít nhất 1 quý để xem kế hoạch.</div>
            )}
          </>
        )}

        <label style={{ display: "block", marginTop: 16, fontSize: 12, color: "var(--text-secondary)" }}>
          Lý do phân bổ <span style={{ color: "var(--accent-red)" }}>*</span>
          <textarea value={allocationReason} onChange={(event) => setAllocationReason(event.target.value)} maxLength={1000} placeholder="Ví dụ: Bù doanh thu bán lẻ chưa phân bổ kỳ này" style={{ marginTop: 5 }} />
        </label>

        {allocMode === "single" && (
          <div style={{ marginTop: 18 }}>
            {targetRevenue <= 0 ? (
              <div className="empty">Chọn số tiền lớn hơn 0 để xem FIFO.</div>
            ) : previewLoading ? (
              <SkeletonTable rows={4} cols={8} />
            ) : previewError ? (
              <div style={{ padding: 14, border: `1px solid ${previewStale ? "var(--accent-amber)" : "var(--accent-red)"}`, borderRadius: 8 }}>
                <b>{previewStale ? "Bản xem trước đã cũ" : "Không thể xem trước"}</b>
                <div style={{ color: "var(--text-secondary)", marginTop: 4 }}>{previewError}</div>
                {previewStale && <button className="btn btn-secondary" style={{ marginTop: 10 }} onClick={() => void reloadStalePreview()}>Tải lại hạn mức</button>}
              </div>
            ) : preview ? (
              <>
                <div style={{ marginBottom: 10, color: "var(--text-secondary)", fontSize: 13 }}>
                  FIFO lô cũ nhất trước · <b>{preview.items.length} lô</b> · Tổng <b>{formatVND(preview.allocated_revenue)}</b>
                </div>
                <div className="table-wrap">
                  <table>
                    <thead><tr><th>Loại</th><th>Lô</th><th>Sản phẩm</th><th>SKU</th><th style={{ textAlign: "right" }}>SL</th><th style={{ textAlign: "right" }}>Giá bán snapshot</th><th style={{ textAlign: "right" }}>Giá trị phân bổ</th><th style={{ textAlign: "right" }}>Còn sau</th></tr></thead>
                    <tbody>
                      {preview.items.map((item) => (
                        <tr key={item.stock_lot_id} style={item.is_adjusted_final ? { background: "rgba(245, 158, 11, 0.12)" } : undefined}>
                          <td><Badge variant={item.sub_type === "vat" ? "ok" : "warn"}>{item.sub_type === "vat" ? "VAT" : "Bảng kê"}</Badge></td>
                          <td>#${item.stock_lot_id}<br /><span style={{ color: "var(--text-tertiary)", fontSize: 11 }}>{formatDate(item.created_at)}</span></td>
                          <td><b>{item.item_name}</b>{item.is_adjusted_final && <><br /><Badge variant="warn">Dòng cuối điều chỉnh để khớp tiền</Badge></>}</td>
                          <td>{item.sku || "—"}</td>
                          <td style={{ textAlign: "right" }}>{formatNumber(item.qty)}</td>
                          <td style={{ textAlign: "right" }}>{formatVND(item.unit_sale_price)}</td>
                          <td style={{ textAlign: "right", fontWeight: 700 }}>{formatVND(item.allocated_value)}</td>
                          <td style={{ textAlign: "right" }}>{formatNumber(item.available_after_qty)} SP</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
          </div>
        )}
      </Modal>

      <Modal open={!!detail} onClose={() => setDetail(null)} title="Chi tiết phân bổ" width={980}>
        {detail && (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10, marginBottom: 16 }}>
              <div><span style={{ color: "var(--text-tertiary)", fontSize: 12 }}>Khoảng ngày</span><br /><b>{detail.date_from} → {detail.date_to}</b></div>
              <div><span style={{ color: "var(--text-tertiary)", fontSize: 12 }}>Phân bổ</span><br /><b>{formatVND(detail.allocated_revenue)}</b></div>
              <div><span style={{ color: "var(--text-tertiary)", fontSize: 12 }}>Người thao tác</span><br /><b>{detail.created_by}</b></div>
              <div><span style={{ color: "var(--text-tertiary)", fontSize: 12 }}>Trạng thái</span><br /><Badge variant={detail.status === "active" ? "ok" : "ghost"}>{detail.status === "active" ? "Hiệu lực" : "Đã đảo"}</Badge></div>
            </div>
            <p style={{ color: "var(--text-secondary)" }}><b>Lý do:</b> {detail.reason}</p>
            {detail.reversal_reason && <p style={{ color: "var(--text-secondary)" }}><b>Lý do đảo:</b> {detail.reversal_reason}</p>}
            {detail.s1a_regen_error && <p style={{ color: "var(--accent-red)" }}><b>Lỗi tái sinh S1A:</b> {detail.s1a_regen_error}</p>}
            <div className="table-wrap">
              <table>
                <thead><tr><th>Loại</th><th>Lô</th><th>Sản phẩm</th><th>SKU</th><th style={{ textAlign: "right" }}>SL</th><th style={{ textAlign: "right" }}>Giá snapshot</th><th style={{ textAlign: "right" }}>Phân bổ</th></tr></thead>
                <tbody>
                  {(detail.items || []).map((item) => (
                    <tr key={item.id}>
                      <td><Badge variant={item.sub_type === "vat" ? "ok" : "warn"}>{item.sub_type === "vat" ? "VAT" : "Bảng kê"}</Badge></td>
                      <td>#{item.stock_lot_id}</td><td>{item.item_name}{item.is_adjusted_final ? <><br /><Badge variant="warn">Điều chỉnh cuối</Badge></> : null}</td><td>{item.sku || "—"}</td>
                      <td style={{ textAlign: "right" }}>{formatNumber(item.qty)}</td><td style={{ textAlign: "right" }}>{formatVND(item.unit_sale_price_snapshot)}</td><td style={{ textAlign: "right", fontWeight: 700 }}>{formatVND(item.allocated_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
              <span style={{ color: "var(--text-tertiary)", fontSize: 12 }}>{(detail.events || []).length} sự kiện audit · Không xóa cứng</span>
              {detail.status === "active" && <button className="btn-danger" onClick={() => { setReverseTarget(detail); setReverseReason(""); setReverseKey(makeIdempotencyKey(`vra-reverse-${detail.id}`)); }}>Lập bút toán đảo</button>}
            </div>
          </>
        )}
      </Modal>

      <Modal
        open={!!reverseTarget}
        onClose={() => !reversing && setReverseTarget(null)}
        title="Lập bút toán đảo"
        width={560}
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setReverseTarget(null)} disabled={reversing}>Huỷ</button>
            <button className="btn-danger" onClick={() => void reverseAllocation()} disabled={!reverseReason.trim() || reversing}>{reversing ? "Đang ghi…" : "Xác nhận bút toán đảo"}</button>
          </>
        }
      >
        <p style={{ marginTop: 0, color: "var(--text-secondary)" }}>
          Không xóa lịch sử gốc. Hạn mức kho ảo được hoàn lại; tồn POS và Loyverse không thay đổi.
        </p>
        <label style={{ fontSize: 12, color: "var(--text-secondary)" }}>
          Lý do đảo <span style={{ color: "var(--accent-red)" }}>*</span>
          <textarea value={reverseReason} onChange={(event) => setReverseReason(event.target.value)} maxLength={1000} placeholder="Nhập lý do bắt buộc" style={{ marginTop: 5 }} />
        </label>
      </Modal>

      <Modal open={!!lotItem} onClose={() => setLotItem(null)} title={lotItem ? `Lô nhập — ${lotItem.item_name}` : ""} width={760}>
        {lotLoading ? (
          <SkeletonTable rows={4} cols={5} />
        ) : lots.length === 0 ? (
          <div className="empty">Chưa có lô nhập (lot mồ côi hoặc SP chưa map đơn).</div>
        ) : (
          <>
            <div style={{ marginBottom: 12, fontSize: 13, color: "var(--text-secondary)" }}>
              Tồn hiện tại: <b>{formatNumber(lotItem?.in_stock ?? 0)}</b> · {lots.length} lô · còn lại: <b>{formatNumber(lots.reduce((sum, lot) => sum + (lot.remaining ?? 0), 0))}</b> (FIFO)
            </div>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Ngày nhập</th><th>Đơn</th><th>Loại</th><th style={{ textAlign: "right" }}>Nhập</th><th style={{ textAlign: "right" }}>Còn</th><th style={{ textAlign: "right" }}>Giá vốn</th><th>NCC</th></tr></thead>
                <tbody>
                  {lots.map((lot) => {
                    const remaining = lot.remaining ?? 0;
                    const exhausted = remaining <= 0;
                    return (
                      <tr key={lot.id} style={{ opacity: exhausted ? 0.5 : 1 }}>
                        <td>{lot.order_date || lot.created_at.slice(0, 10)}</td><td>{lot.import_order_id ? `#${lot.import_order_id}` : "—"}</td>
                        <td>{lot.is_vat ? <Badge variant="ok">VAT</Badge> : lot.is_bangke ? <Badge variant="warn">BK</Badge> : "—"}</td>
                        <td style={{ textAlign: "right" }}>{formatNumber(lot.quantity)}</td><td style={{ textAlign: "right", fontWeight: exhausted ? 400 : 700, color: exhausted ? "var(--text-tertiary)" : "var(--accent-emerald)" }}>{exhausted ? "hết" : formatNumber(remaining)}</td>
                        <td style={{ textAlign: "right" }}>{formatVND(lot.unit_cost)}</td><td style={{ fontSize: 12, color: "var(--text-tertiary)" }}>{lot.supplier_name || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 10, fontSize: 11, color: "var(--text-tertiary)" }}>FIFO: lô cũ nhất tiêu trước. "Còn" = số lượng chưa bán. Lô hết = mờ.</div>
          </>
        )}
      </Modal>
    </>
  );
}
